import { Dispatch, SetStateAction, useCallback, useState } from 'react';

export type ReturnDisclosureWithData<T> = {
  isOpen: boolean;
  data: T | null;
  open: (data?: T | null) => void;
  close: () => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setData: Dispatch<SetStateAction<T | null>>;
  onExitComplete: () => void;
  shouldRender: boolean;
};

type UseDisclosureWithDataParams<Type> = {
  initial?: boolean;
  initialData?: Type | null;
  onClose?: () => void;
};

export function useDisclosureWithData<Type>({
  initial = false,
  initialData = null,
  onClose,
}: UseDisclosureWithDataParams<Type> = {}): ReturnDisclosureWithData<Type> {
  const [isOpen, setIsOpen] = useState<boolean>(initial);
  const [data, setData] = useState<Type | null>(initialData);
  const [isExitCompleted, setIsExitCompleted] = useState(true);
  const open = useCallback((data: Type | null = null) => {
    setData(data);
    setIsOpen(true);
    setIsExitCompleted(false);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setData(null);
    onClose?.();
  }, [onClose]);

  const onExitComplete = () => {
    setIsExitCompleted(true);
  };
  const shouldRender = isOpen || !isExitCompleted;

  return {
    isOpen,
    data,
    open,
    close,
    setIsOpen,
    setData,
    onExitComplete,
    shouldRender,
  };
}
